import { computed, nextTick, onMounted, onUnmounted, type Ref, unref, watch } from 'vue';
import { useNuxtApp } from 'nuxt/app';
import { PUSH_VIEW_PRODUCT_LIST } from '~~/common/plugins/aw-analytics';
import { AnalyticsBannerInteraction, type PromotionSchema } from '~~/common/utils/AnalyticsBannerInteraction';
import { useAnalyticsProductListQueueStore } from '~~/common/stores/analyticsProductListQueue';
import type { ProductData } from '~~/common/stores/product';

export interface UseProductListAnalyticsOptions {
  componentEl: Ref<HTMLElement | null>;
  products: Ref<(ProductData & { isPlaceholder?: boolean })[] | Record<string, (ProductData & { isPlaceholder?: boolean })[]>>;
  analyticsBannerInteraction?: PromotionSchema | null;
  itemListName?: string | null | Ref<string | null>;
  getProductIndex?: (product: ProductData) => number;
}

export function useProductListAnalytics ({
  componentEl,
  products,
  analyticsBannerInteraction = null,
  itemListName = null,
  getProductIndex,
}: UseProductListAnalyticsOptions) {
  const { $awAnalytics } = useNuxtApp();
  const analyticsProductListQueueStore = useAnalyticsProductListQueueStore();
  let intersectionObserver: IntersectionObserver | null = null;

  const itemListNameRef = computed(() => unref(itemListName));

  function sendViewItemListAnalytics (visibleProducts: { product: ProductData; productIndex: number }[]) {
    $awAnalytics[PUSH_VIEW_PRODUCT_LIST]({
      products: visibleProducts.map(({ product, productIndex }) => ({
        product,
        itemListId: analyticsBannerInteraction
          ? AnalyticsBannerInteraction.format({ ...analyticsBannerInteraction, promotionBannerIndex: productIndex + 1 })?.promotion_id
          : undefined,
      })),
      itemListName: itemListNameRef.value || '',
      searchText: [],
    });
  }

  function setupIntersectionObserver () {
    if (import.meta.server) {
      return;
    }

    if (intersectionObserver) {
      intersectionObserver.disconnect();
    }

    intersectionObserver = new IntersectionObserver((entries) => {
      const productList = Array.isArray(products.value)
        ? products.value
        : Object.values(products.value).flat();

      const newlyVisibleProducts: { product: ProductData; productIndex: number }[] = [];

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const productCard = entry.target;
          const productId = productCard.getAttribute('data-product-id');
          const productIndex = productCard.getAttribute('data-product-index');

          if (productId && !analyticsProductListQueueStore.isProductInViewedHistory(productId, itemListNameRef.value)) {
            analyticsProductListQueueStore.addToViewedProductListHistory(productId, itemListNameRef.value);

            const product = productList.find((p) => p?.id?.toString() === productId);

            if (product && !product.isPlaceholder) {
              newlyVisibleProducts.push({ product, productIndex: productIndex ? parseInt(productIndex, 10) : 0 });
            }
          }
        }
      });

      if (newlyVisibleProducts.length) {
        sendViewItemListAnalytics(newlyVisibleProducts);
      }
    }, {
      root: null,
      rootMargin: '0px',
      threshold: 0.5,
    });

    nextTick(() => {
      observeProductCards();
    });
  }

  function observeProductCards () {
    if (!intersectionObserver || !componentEl.value) {
      return;
    }

    const productCards = componentEl.value.querySelectorAll('[data-product-id]');
    productCards.forEach((card) => {
      intersectionObserver!.observe(card);
    });
  }

  function getItemListId (productIndex: number) {
    if (!analyticsBannerInteraction) {
      return null;
    }

    const bannerInteraction = {
      ...analyticsBannerInteraction,
      promotionBannerIndex: productIndex + 1,
    };

    return AnalyticsBannerInteraction.format(bannerInteraction)?.promotion_id;
  }

  function getItemListName () {
    return itemListNameRef.value || null;
  }

  function getGlobalProductIndex (product: ProductData): number {
    if (getProductIndex) {
      return getProductIndex(product);
    }

    const productList = Array.isArray(products.value)
      ? products.value
      : Object.values(products.value).flat();

    const index = productList.findIndex((p) => p?.id === product?.id);
    return index !== -1 ? index : 0;
  }

  onMounted(() => {
    setupIntersectionObserver();
  });

  onUnmounted(() => {
    if (intersectionObserver) {
      intersectionObserver.disconnect();
      intersectionObserver = null;
    }
  });

  watch(() => products.value, () => {
    nextTick(() => {
      observeProductCards();
    });
  }, { deep: true });

  return {
    getItemListId,
    getItemListName,
    getGlobalProductIndex,
    observeProductCards,
  };
}
